/**
 * Service Permissions Utility
 * Handles service permissions for malls
 */

// Service definitions with English names
export const SERVICES = {
  CARGO: 'Cargo',
  CLICK_AND_COLLECT: 'ClickAndCollect',
  CORRESPONDENCE: 'Correspondence',
  GOODS_ACCEPTANCE: 'GoodsAcceptance',
  SAFETY_LOCKER: 'SafetyLocker',
  SAFETY_CLOAKROOM: 'SafetyCloakroom',
  HOME_DELIVERY: 'HomeDelivery',
  TAX_FREE: 'TaxFree',
  PET_CAR: 'PetCar',
  KID_CAR: 'KidCar',
  HANDSFREE: 'Handsfree',
  POST_BOX: 'PostBox',
  EASY_MALL: 'EasyMall',
  GIFT: 'Gift',
  STATISTICS: 'Statistics',
};

// Service labels for UI (Turkish)
export const SERVICE_LABELS = {
  [SERVICES.CARGO]: 'Kargo',
  [SERVICES.CLICK_AND_COLLECT]: 'Click And Collect',
  [SERVICES.CORRESPONDENCE]: 'Muhaberat',
  [SERVICES.GOODS_ACCEPTANCE]: 'Mal Kabul',
  [SERVICES.SAFETY_LOCKER]: 'Emanet Dolap',
  [SERVICES.SAFETY_CLOAKROOM]: 'Emanet Vestiyer',
  [SERVICES.HOME_DELIVERY]: 'Eve Teslim',
  [SERVICES.TAX_FREE]: 'Tax Free',
  [SERVICES.PET_CAR]: 'Evcil Hayvan Aracı',
  [SERVICES.KID_CAR]: 'Çocuk Aracı',
  [SERVICES.HANDSFREE]: 'Handsfree',
  [SERVICES.POST_BOX]: 'PostBox',
  [SERVICES.EASY_MALL]: 'Easy Mall',
  [SERVICES.GIFT]: 'Hediye Paketi',
  [SERVICES.STATISTICS]: 'İstatistikler',
};

// Get all services as array
export const getAllServices = () => Object.values(SERVICES);

// Get service label
export const getServiceLabel = (serviceKey) => SERVICE_LABELS[serviceKey] || serviceKey;

/**
 * Parse activeServices string to array
 * @param {string} activeServicesString - Comma-separated service names
 * @returns {array} Array of service names
 */
export const parseActiveServices = (activeServicesString) => {
  if (!activeServicesString) return [];
  if (typeof activeServicesString === 'string') {
    return activeServicesString.split(',').map(s => s.trim()).filter(s => s);
  }
  if (Array.isArray(activeServicesString)) {
    return activeServicesString;
  }
  return [];
};

/**
 * Convert active services array to string
 * @param {array} servicesArray - Array of service names
 * @returns {string} Comma-separated service names
 */
export const convertServicesToString = (servicesArray) => {
  if (!Array.isArray(servicesArray)) return '';
  return servicesArray.filter(s => s).join(',');
};

/**
 * Check if user has permission for a service
 * @param {string} mallID - Mall ID
 * @param {string} serviceName - Service name (English)
 * @param {object} mallData - Mall data from API
 * @returns {boolean} Whether user has permission
 */
export const checkServicePermission = (mallID, serviceName, mallData) => {
  if (!mallData || !mallData.activeServices) {
    return false;
  }

  const activeServices = parseActiveServices(mallData.activeServices);
  return activeServices.includes(serviceName);
};

/**
 * Get user permissions for all services
 * @param {string} mallID - Mall ID
 * @param {object} mallData - Mall data from API
 * @returns {object} Object with service names as keys and boolean permissions as values
 */
export const getUserServicePermissions = (mallID, mallData) => {
  const permissions = {};
  const allServices = getAllServices();

  allServices.forEach(service => {
    permissions[service] = checkServicePermission(mallID, service, mallData);
  });

  return permissions;
};

